/*
 * ============================================================
 * SAV_ERP — Smart Account Vision Enterprise Resource Planning
 * Copyright (c) 2026 Hatem Mohamed Abdalla.
 * All rights reserved.
 *
 * This file is part of a proprietary software product owned
 * solely by Hatem Mohamed Abdalla (UAE, +971 50 390 9781).
 * Copying, redistribution, modification, reverse engineering
 * or reuse in any form without prior written consent of the
 * copyright owner is strictly prohibited and will be
 * prosecuted under UAE Federal Law No. 38/2021 on Copyright
 * and applicable international treaties.
 *
 * Fingerprint: SAV-ERP-HMA-2026-#a7f3d0
 * ============================================================
 */
import React, { useState, useEffect } from 'react';
import { apiGet, apiPost, apiPut } from '../services/apiClient';
import { extractErrorMessage, logError } from '../utils/errorHandler';

const AccountModal = ({ 
  isOpen, 
  onClose, 
  account = null, 
  onSaved, 
  t, 
  language 
}) => {
  const [formData, setFormData] = useState({
    account_code: account?.account_code || '',
    account_name: account?.account_name || '',
    account_name_ar: account?.account_name_ar || '',
    account_type: account?.account_type || 'assets',
    account_subtype: account?.account_subtype || 'current_assets',
    parent_account_id: account?.parent_account_id || '',
    opening_balance: account?.opening_balance || 0,
    description: account?.description || '',
    description_ar: account?.description_ar || ''
  });
  
  const [parentAccounts, setParentAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const accountTypes = [
    { value: 'assets', label: 'Assets', label_ar: 'الأصول' },
    { value: 'liabilities', label: 'Liabilities', label_ar: 'الخصوم' },
    { value: 'equity', label: 'Equity', label_ar: 'حقوق الملكية' },
    { value: 'revenue', label: 'Revenue', label_ar: 'الإيرادات' },
    { value: 'expenses', label: 'Expenses', label_ar: 'المصروفات' }
  ];

  const accountSubtypes = {
    assets: [
      { value: 'current_assets', label: 'Current Assets', label_ar: 'الأصول الجارية' },
      { value: 'fixed_assets', label: 'Fixed Assets', label_ar: 'الأصول الثابتة' },
      { value: 'investments', label: 'Investments', label_ar: 'الاستثمارات' }
    ],
    liabilities: [
      { value: 'current_liabilities', label: 'Current Liabilities', label_ar: 'الخصوم الجارية' },
      { value: 'long_term_liabilities', label: 'Long-term Liabilities', label_ar: 'الخصوم طويلة الأجل' }
    ],
    equity: [
      { value: 'capital', label: 'Capital', label_ar: 'رأس المال' },
      { value: 'retained_earnings', label: 'Retained Earnings', label_ar: 'الأرباح المحتجزة' }
    ],
    revenue: [
      { value: 'sales_revenue', label: 'Sales Revenue', label_ar: 'إيرادات المبيعات' },
      { value: 'service_revenue', label: 'Service Revenue', label_ar: 'إيرادات الخدمات' },
      { value: 'other_income', label: 'Other Income', label_ar: 'إيرادات أخرى' }
    ],
    expenses: [
      { value: 'cost_of_goods_sold', label: 'Cost of Goods Sold', label_ar: 'تكلفة البضاعة المباعة' },
      { value: 'operating_expenses', label: 'Operating Expenses', label_ar: 'المصروفات التشغيلية' },
      { value: 'financial_expenses', label: 'Financial Expenses', label_ar: 'مصروفات مالية' },
      { value: 'other_expenses', label: 'Other Expenses', label_ar: 'مصروفات أخرى' }
    ]
  };

  useEffect(() => {
    if (isOpen) {
      loadParentAccounts();
    }
  }, [isOpen]);

  const loadParentAccounts = async () => {
    try {
      const accounts = await apiGet('/accounts');
      const parents = accounts.filter(acc => acc.is_parent);
      setParentAccounts(parents);
    } catch (error) {
      logError(error, 'AccountModal.loadParentAccounts');
    }
  };

  // التحقق من صحة البيانات المحسن
  const validateForm = () => {
    const errors = [];
    
    // التحقق من الكود
    if (!formData.account_code.trim()) {
      errors.push('كود الحساب مطلوب');
    } else if (!/^\d{4,6}$/.test(formData.account_code.trim())) {
      errors.push('كود الحساب يجب أن يكون من 4-6 أرقام');
    }
    
    // التحقق من الأسماء
    if (!formData.account_name.trim()) {
      errors.push('اسم الحساب بالإنجليزية مطلوب');
    } else if (formData.account_name.trim().length < 2) {
      errors.push('اسم الحساب يجب أن يكون على الأقل حرفين');
    }
    
    if (!formData.account_name_ar.trim()) {
      errors.push('اسم الحساب بالعربية مطلوب');
    } else if (formData.account_name_ar.trim().length < 2) {
      errors.push('اسم الحساب العربي يجب أن يكون على الأقل حرفين');
    }
    
    // التحقق من عدم تطابق الأسماء
    if (formData.account_name.trim() === formData.account_name_ar.trim()) {
      errors.push('يجب أن يكون الاسم الإنجليزي مختلفاً عن الاسم العربي');
    }
    
    // التحقق من نوع الحساب
    if (!formData.account_type) {
      errors.push('نوع الحساب مطلوب');
    }
    
    // التحقق من النوع الفرعي
    if (!formData.account_subtype) {
      errors.push('النوع الفرعي للحساب مطلوب');
    }
    
    // التحقق من الرصيد الافتتاحي
    if (isNaN(formData.opening_balance) || formData.opening_balance < 0) {
      errors.push('الرصيد الافتتاحي يجب أن يكون رقماً غير سالب');
    }
    
    return errors;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'opening_balance' 
        ? parseFloat(value) || 0 
        : value
    }));

    // Reset subtype when type changes
    if (name === 'account_type') {
      const defaultSubtype = accountSubtypes[value]?.[0]?.value || '';
      setFormData(prev => ({
        ...prev,
        [name]: value,
        account_subtype: defaultSubtype
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // التحقق من صحة البيانات
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setError(validationErrors.join(' • '));
      setLoading(false);
      return;
    }

    try {
      if (account) {
        // Update existing account
        await apiPut(`/accounts/${account.id}`, formData);
      } else {
        // Create new account
        await apiPost('/accounts', formData);
      }
      
      onSaved();
      onClose();
    } catch (error) {
      logError('Account Save', error);
      setError(extractErrorMessage(error, 'فشل في حفظ الحساب'));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const currentSubtypes = accountSubtypes[formData.account_type] || [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-full overflow-y-auto">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {account ? t('edit') + ' ' + t('accountName') : t('addAccount')}
        </h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('accountCode')} *
              </label>
              <input
                type="text"
                name="account_code"
                value={formData.account_code}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
                disabled={account} // Don't allow editing account code
                placeholder="e.g., 120100"
              />
              <p className="text-xs text-gray-500 mt-1">
                Assets: 1000-1999, Liabilities: 2000-2999, Equity: 3000-3999, Revenue: 4000-4999, Expenses: 5000+
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('accountType')} *
              </label>
              <select
                name="account_type"
                value={formData.account_type}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              >
                {accountTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {language === 'ar' ? type.label_ar : type.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Account Subtype *
              </label>
              <select
                name="account_subtype"
                value={formData.account_subtype}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              >
                {currentSubtypes.map(subtype => (
                  <option key={subtype.value} value={subtype.value}>
                    {language === 'ar' ? subtype.label_ar : subtype.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Parent Account
                <small className="text-gray-500 block">(الحساب الرئيسي - اختياري)</small>
              </label>
              <select
                name="parent_account_id"
                value={formData.parent_account_id}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">None (Top Level Account)</option>
                {parentAccounts.map(parentAccount => (
                  <option key={parentAccount.id} value={parentAccount.id}>
                    {parentAccount.account_code} - {
                      language === 'ar' ? parentAccount.account_name_ar : parentAccount.account_name
                    }
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                اختر الحساب الرئيسي إذا كان هذا حساب فرعي
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('accountName')} (English) *
              </label>
              <input
                type="text"
                name="account_name"
                value={formData.account_name}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('accountName')} (العربية) *
              </label>
              <input
                type="text"
                name="account_name_ar"
                value={formData.account_name_ar}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('openingBalance')} (AED)
              </label>
              <input
                type="number"
                name="opening_balance"
                value={formData.opening_balance}
                onChange={handleInputChange}
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('description')} (English)
              </label>
              <input
                type="text"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('description')} (العربية)
              </label>
              <input
                type="text"
                name="description_ar"
                value={formData.description_ar}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              {t('cancel')}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {loading ? t('loading') : (account ? t('save') : t('create'))}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AccountModal;